import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import {
    tryToCreateAiModel,
    tryToEditAiModel,
    tryToFetchSingleAiModel
} from '../../../../store/brokers/partners/ai-models/aiModelsSlice';
import Switch from '../../../../shared/switch';
import Button from '../../../../shared/button';
import Error from '../../../../shared/error';
import Modal from '../../../../shared/modal';
import Input from '../../../../shared/input';


interface IAssetTemplateFrameModal {
    openModal: boolean;
    itemId: string;
    handleCloseModal: () => void;
    onSaveChanges: (type: string, message: string) => void;
}

const ManageModelsModal = ({
    openModal,
    itemId,
    handleCloseModal,
    onSaveChanges
}: IAssetTemplateFrameModal) => {
    const dispatch = useAppDispatch();
    const [saveError, setSaveError] = useState<string>()
    const [filesSelected, setFilesSelected] = useState<any>()
    const [existingImage, setExistingImage] = useState<any>()
    const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false)
    const [filePreview, setFilePreview] = useState<string | undefined>();
    const [enabled, setEnabled] = useState<boolean>(false)
    const [apiUrl, setApiUrl] = useState<string>();
    const [apiKey, setApiKey] = useState<string>();
    const [name, setName] = useState<string>();
    // const loading = useAppSelector((state) => state?.aiModels.aiModelIsLoading)
    const details = useAppSelector((state) => state?.aiModels?.aiModel)

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (!acceptedFiles?.length) return;
        const file = acceptedFiles[0];

        const reader = new FileReader();
        reader.onload = (event: ProgressEvent<FileReader>) => {
            const result = event?.target?.result as string;
            setFilePreview(result);
            setFilesSelected(file);
        };
        reader.readAsDataURL(file);
    }, []);

    const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false });

    useEffect(() => {
        if (itemId) {
            dispatch(tryToFetchSingleAiModel(itemId)).unwrap()
        }
    }, [itemId])

    useEffect(() => {
        if (itemId && details) {
            setName(details?.name)
            setApiKey(details?.apiKey)
            setApiUrl(details?.apiUrl)
            setExistingImage(details?.logoUrl)
            setEnabled(details?.enabled || false)
        }
    }, [itemId, details])

    const onCloseModal = () => {
        handleCloseModal();
        setSaveError(undefined)
        setFilesSelected(undefined)
        setFilePreview(undefined)
        setName(undefined)
        setApiKey(undefined)
        setEnabled(false)
        setApiUrl(undefined)
        setLoadingSubmit(false)
    }

    const onDeleteImage = () => {
        setFilesSelected(undefined);
        setFilePreview(undefined);
    };

    const onDeleteExistingImage = () => {
        setExistingImage(undefined)
    }

    const onSubmit = async () => {
        setLoadingSubmit(true)
        if (!filesSelected && !existingImage) {
            setSaveError('Please upload AI Model icon')
            setLoadingSubmit(false)
            return
        }
        if (!name) {
            setSaveError('Name is mandatory')
            setLoadingSubmit(false)
            return
        }
        try {
            const dataForm: any = new FormData()
            if (filesSelected) {
                dataForm.append(`file`, filesSelected)
            }
            dataForm.append(`enabled`, `${enabled}`)
            if (apiKey) {
                dataForm.append(`apiKey`, apiKey)
            }
            if (apiUrl) {
                dataForm.append(`apiUrl`, apiUrl)
            }
            dataForm.append(`name`, name)

            if (itemId) {
                await dispatch(tryToEditAiModel({ id: itemId, data: dataForm })).unwrap()
            } else {
                await dispatch(tryToCreateAiModel(dataForm)).unwrap()
            }
            onSaveChanges('success', 'AI Model successfully saved!')
            onCloseModal()
        } catch (err) {
            setSaveError(`${err}`)
            setLoadingSubmit(false)
        }
    }

    const onSetValue = (value: any, type?: string) => {
        if (type) {
            switch (type) {
                case 'name':
                    setName(value)
                    break;
                case 'apiKey':
                    setApiKey(value)
                    break;
                case 'apiUrl':
                    setApiUrl(value)
                    break;
            }
        }
    }

    return (
        <Modal
            open={openModal}
            onClose={onCloseModal}
            contentContainerStyle={'!min-w-[700px]'}
            dataQa={'close-button'}>
            <div className={'p-2 min-w-[550px] mx-10'}>
                <p className='mb-4 font-semibold text-sm dark:text-primary-dark'>AI Model</p>
                <div className='mt-6 mb-10'>
                    {saveError && <Error text={saveError} />}
                    <Input
                        label='Name'
                        placeholder=' '
                        inputValue={name}
                        showValue={true}
                        inputUniqueName='name'
                        onChangeInput={onSetValue}
                    />
                    <Input
                        label='API URL'
                        placeholder=' '
                        inputValue={apiUrl}
                        showValue={true}
                        inputUniqueName='apiUrl'
                        onChangeInput={onSetValue}
                    />
                    <Input
                        label='API Key'
                        placeholder=' '
                        inputValue={apiKey}
                        showValue={true}
                        inputUniqueName='apiKey'
                        onChangeInput={onSetValue}
                    />
                    <Switch
                        beforeText='Enable'
                        checked={enabled}
                        onToggleSwitch={() => setEnabled(!enabled)}
                    />
                    <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        <Button
                            // icon={<img src='/assets/shared/upload-file.svg' className='w-[30px] object-contains pr-2' />}
                            className={'btn-main dark:!bg-black dark:!text-white !py-2 !shadow-none flex flex-row items-center mt-5'}
                            label={'Select File'}
                            dataQa={'select-file'}
                        />
                    </div>
                    <div className='flex flex-rows items-center flex-wrap mt-6'>
                        {filePreview && (
                            <div
                                onClick={onDeleteImage}
                                className={'relative flex flex-col items-center mr-4 z-70'}>
                                <div data-qa={'delete-image'} className='absolute top-[-7px] right-[-7px] cursor-pointer z-40'>
                                    <img src={'/assets/shared/cancel.svg'} className='max-w-[15px] object-contain z-70' />
                                </div>
                                <div className='border border-[#000] p-1 z-0'>
                                    <img src={filePreview} className='max-w-[70px] object-contain' />
                                </div>
                            </div>
                        )}
                        {existingImage &&
                            <div
                                onClick={() => onDeleteExistingImage()}
                                className={'relative flex flex-col items-center mr-4 z-70'}>
                                <div data-qa={'delete-image'} className='absolute top-[-7px] right-[-7px] cursor-pointer z-40'>
                                    <img src={'/assets/shared/cancel.svg'} className='max-w-[15px] object-contain z-70' />
                                </div>
                                <div className='border border-[#000] p-1 z-0'>
                                    <img src={existingImage} className='max-w-[70px] object-contain' />
                                </div>
                            </div>
                        }

                    </div>
                </div>
                <div className='flex flex-row justify-center mb-2'>
                    <Button
                        label='Submit'
                        dataQa={'submit-button'}
                        className={`${loadingSubmit ? 'btn-main-disable' : 'btn-main dark:!bg-black dark:!text-white dark:!border-black'} !shadow-none min-w-[150px]`}
                        onClickButton={() => !loadingSubmit && onSubmit()}
                    />
                </div>
            </div>
        </Modal>
    )
}

export default ManageModelsModal;